/**
 * O teste que a tela acabou de disparar, para reconhecer quando o resultado dele chegar.
 *
 * `dataHoraTesteAnterior` é a data do último resultado que a tela conhecia antes de salvar;
 * `solicitadoEm` é o instante do clique, no relógio do navegador.
 */
export type ReferenciaDoTeste = {
  dataHoraTesteAnterior: string | null;
  solicitadoEm: number;
};

/** Folga para diferença entre o relógio do navegador e o do servidor. */
export const TOLERANCIA_RELOGIO_MS = 2 * 60 * 1000;

const paraMillis = (data?: string | null): number | null => {
  if (!data) {
    return null;
  }
  const millis = new Date(data).getTime();
  return isNaN(millis) ? null : millis;
};

/**
 * Se o status recebido já é do teste que a tela disparou, e não de uma tentativa anterior.
 *
 * Salvar a configuração dispara o envio do e-mail de teste de forma assíncrona e responde
 * antes de ele sair. A consulta de status feita logo em seguida chega ao mailreader enquanto
 * ele ainda guarda o resultado da tentativa anterior — em produção, a leitura aconteceu
 * 160 ms antes de o e-mail ser enviado. Se a anterior tinha terminado em ERRO, a tela
 * mostrava "Refaça a regra" na hora e parava de consultar, sem nunca ver o teste novo.
 *
 * O critério principal compara duas datas do servidor — o `dataHoraTeste` que a tela já
 * conhecia e o recebido —, então não depende do relógio do navegador: o mailreader grava
 * uma data nova a cada teste. Só quando a tela não conhecia resultado anterior é que a
 * comparação cai para o instante do clique, com folga para diferença de relógio.
 *
 * Sem data no resultado não há como afirmar que ele é novo: conta como antigo.
 */
export const pertenceAoTesteSolicitado = (
  dataHoraTeste: string | null | undefined,
  referencia: ReferenciaDoTeste
): boolean => {
  const recebido = paraMillis(dataHoraTeste);
  if (recebido === null) {
    return false;
  }

  const anterior = paraMillis(referencia.dataHoraTesteAnterior);
  if (anterior !== null) {
    return recebido > anterior;
  }

  return recebido >= referencia.solicitadoEm - TOLERANCIA_RELOGIO_MS;
};
